import type { ReactNode } from 'react';
import { Link, type LinkProps, useRouterState } from '@tanstack/react-router';

import { AppShell, Navbar, Sidebar, SidebarProvider } from '@keboola/design';
import { type LucideIcon } from '@keboola/design/icons';

import { AccountMenu } from './AccountMenu';
import { type Section } from './accountMenuModel';
import { AppWordmark } from './AppWordmark';
import { ColorSchemeMenu } from './ColorSchemeMenu';

export type DashboardNavItem = {
  /** Route-tree typed: `string` would bypass the `Register` augmentation in `main.tsx`. */
  to: NonNullable<LinkProps['to']>;
  label: string;
  icon: LucideIcon;
};

type Props = {
  section: Section;
  nav: readonly DashboardNavItem[];
  children: ReactNode;
};

/**
 * Label and root per section.
 *
 * - Not derived from `nav`: gated / loading / access-denied states render with an empty `nav`.
 * - Active-state rules depend on the root, so rail order must not change it.
 */
const SECTIONS: Record<Section, { label: string; root: NonNullable<LinkProps['to']> }> = {
  vendor: { label: 'Vendor', root: '/~' },
  admin: { label: 'Admin', root: '/admin' },
};

/** Segment-aware prefix match: `/admin-tools` is not under `/admin`; trailing slashes ignored. */
const isUnder = (pathname: string, to: string): boolean => {
  const here = pathname.replace(/\/+$/, '');
  const target = to.replace(/\/+$/, '');
  return here === target || here.startsWith(`${target}/`);
};

/** Shared shell for the signed-in audiences (vendor dashboard and admin panel). */
export function DashboardChrome({ section, nav, children }: Props) {
  // Pathname only, not `useMatchRoute`: `admin/index.tsx` updates search params on every keystroke.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { label: sectionLabel, root: sectionRoot } = SECTIONS[section];
  // The section root is lit exactly when no sibling claims the route.
  const siblingIsActive = nav.some((item) => item.to !== sectionRoot && isUnder(pathname, item.to));

  return (
    <SidebarProvider defaultLeftMode="expanded" leftVariant="inset" leftCollapsible="icon">
      {/* `headerSpan="full"`: the header spans above the rail, not only the content column. */}
      <AppShell headerSpan="full">
        <AppShell.Header flush>
          <Navbar bordered className="tw:flex-1">
            {/* Gutter on the slot: `Navbar`'s cva `pl-2` does not tailwind-merge. Aligns with rail icons. */}
            <Navbar.Header className="tw:mr-6 tw:shrink-0 tw:pl-2.5">
              <AppWordmark />
            </Navbar.Header>

            <Navbar.Content>
              <Navbar.Menu marker="forward-slash">
                <Navbar.MenuItem>
                  <Navbar.MenuButton asChild>
                    <Link to={sectionRoot}>{sectionLabel}</Link>
                  </Navbar.MenuButton>
                </Navbar.MenuItem>
              </Navbar.Menu>
            </Navbar.Content>

            {/* No padding: `Navbar` already carries `pr-3`. */}
            <Navbar.Footer>
              <ColorSchemeMenu />
              <AccountMenu section={section} />
            </Navbar.Footer>
          </Navbar>
        </AppShell.Header>

        <AppShell.Sidebar>
          <Sidebar side="left">
            <Sidebar.Content>
              {/* Landmark for the rail (Sidebar is plain divs), labelled apart from the Navbar's. */}
              <nav aria-label={`${sectionLabel} sections`}>
                <Sidebar.Menu>
                  {nav.map((item) => (
                    <Sidebar.MenuItem key={item.to}>
                      <Sidebar.MenuButton asChild tooltip={item.label}>
                        {/* ⚠️ `activeOptions`, not a manual `aria-current`: `Link` sets it itself.
                            See `siblingIsActive`. */}
                        <Link
                          to={item.to}
                          activeOptions={{ exact: item.to === sectionRoot && siblingIsActive }}
                        >
                          <Sidebar.MenuIcon>
                            <item.icon />
                          </Sidebar.MenuIcon>
                          {item.label}
                        </Link>
                      </Sidebar.MenuButton>
                    </Sidebar.MenuItem>
                  ))}
                </Sidebar.Menu>
              </nav>
            </Sidebar.Content>
          </Sidebar>
        </AppShell.Sidebar>

        {/*
          ⚠️ `relative` keeps the shell to one scrollbar.
          - Radix `Select` / `Combobox` hidden absolute inputs otherwise escape Main's `overflow-auto`
            and stretch the document.
          - Not on `AppShell`: kbc-ui relies on Main not anchoring (`min-h-svh` note in its `App.tsx`).
          - Not a transform: `position: fixed` descendants must keep anchoring to the viewport.
        */}
        <AppShell.Main className="tw:relative">{children}</AppShell.Main>
      </AppShell>
    </SidebarProvider>
  );
}
